import { AlertTriangle } from "lucide-react";

import { cn } from "@/lib/utils";

import { warnTagStyle } from "../tags";

/**
 * Warn-tag chips (🔴 Gấp · 🟣 Đặc biệt · ⭐ KOL) in the order given. `labels` is
 * meant to come from `warnTags()`, which already fixes the order and drops
 * duplicates. `sm` is the compact size for schedule rows.
 */
export function WarnTagChips({
  labels,
  size = "md",
  className,
}: {
  labels: readonly string[];
  size?: "md" | "sm";
  className?: string;
}) {
  if (labels.length === 0) return null;
  return (
    <span className={cn("inline-flex flex-wrap items-center gap-[5px]", className)}>
      {labels.map((label) => {
        const style = warnTagStyle(label);
        return (
          <span
            key={label}
            className={cn(
              "inline-flex items-center whitespace-nowrap rounded-full border font-bold leading-[1.3]",
              "[border-color:color-mix(in_srgb,currentColor_30%,transparent)]",
              size === "sm" ? "px-[7px] py-0.5 text-[11px]" : "px-[9px] py-[3px] text-xs",
            )}
            style={{ color: style.color, backgroundColor: style.bg }}
          >
            {label}
          </span>
        );
      })}
    </span>
  );
}

/**
 * The "Lưu ý ca này" strip on a shift card. Renders nothing for a shift without
 * warn tags, so an untagged card looks exactly as before. The heading is always
 * spelled out next to the icon: the warning must not rely on colour alone.
 */
export function WarnStrip({ labels, className }: { labels: readonly string[]; className?: string }) {
  if (labels.length === 0) return null;
  return (
    <div
      role="note"
      className={cn(
        "flex flex-wrap items-center gap-x-2 gap-y-1.5 rounded-md border px-2.5 py-2",
        "[background:var(--color-danger-bg)] [border-color:color-mix(in_srgb,var(--color-danger)_35%,transparent)]",
        className,
      )}
    >
      <span className="inline-flex items-center gap-1.5 text-[12.5px] font-bold [color:var(--color-danger-text)]">
        <AlertTriangle size={15} aria-hidden />
        Lưu ý ca này
      </span>
      <WarnTagChips labels={labels} />
    </div>
  );
}
